// Reqs editor warnings for required classes (Site Access overhaul slice D,
// George approved "fix all" 2026-09-23). Pure ESM; pinned by
// server/test/reqCodeCheck.test.js.
//
// Why: a coded requirement matches ONLY by certificate_no
// (utils/certMatch.js on the server), so a misspelled code turns every holder
// red, and a course code typed into the NAME box with the code left blank
// reads as a class name. Both happened on live rows: Flint Hills had 03BZ /
// 03H2S / 03MRSEC typed as names, Cenovus Lima carried SAFER where HASC writes
// SE-SAFER. These are WARNINGS at entry, never a block on save.
//
// The server answers which codes anyone holds
// (GET /api/customer-requirements/known-codes, utils/reqCodeCheck.js).

// Council course-code shapes: HASC/CSCCB style (two digits then letters,
// 03BZ, 19FPCGP1) and prefix-dash style (SE-SAFE, SE-SAFER). One word only.
// A plain word or an acronym such as BENZENE or H2S is a legitimate class
// name and is NOT flagged.
const CODE_SHAPES = [/^\d{2}[a-z][a-z0-9]*$/i, /^[a-z]{2,4}-[a-z0-9]+$/i];
export function looksLikeCourseCode(name) {
  const s = String(name == null ? '' : name).trim();
  if (!s || /\s/.test(s) || s.length > 20) return false;
  return CODE_SHAPES.some(re => re.test(s));
}

// A code box's "/"-separated equivalent alternates (the server reads it the
// same way: utils/certMatch.js alternates).
export const codeAlternates = (code) => String(code == null ? '' : code).split('/')
  .map(s => s.replace(/^[\s*•-]+/, '').trim()).filter(Boolean);

// Every token worth asking the server about, upper-cased and sorted: each
// code alternate, plus a code-less ONE-WORD name (it may be a course code in
// the wrong box). Commas are the query separator, so a token holding one is
// never sent.
export function codesToCheck(pairs) {
  const out = new Set();
  for (const p of pairs || []) {
    const alts = codeAlternates(p && p.code);
    if (alts.length) {
      for (const a of alts) out.add(a.toUpperCase());
    } else {
      const n = String((p && p.name) || '').trim();
      if (n && !/\s/.test(n) && n.length <= 60) out.add(n.toUpperCase());
    }
  }
  return [...out].filter(c => c.length <= 60 && !c.includes(',')).sort();
}

// Warnings for one {name, code} row. report = the server's answer, keyed by
// the upper-cased token: { known, holders }. A token the server has not
// answered for yet gets no "unknown code" warning (never a false alarm while
// the lookup is in flight or failed).
export function rowWarnings({ name, code } = {}, report = {}) {
  const out = [];
  const n = String(name == null ? '' : name).trim();
  const alts = codeAlternates(code);
  const at = (t) => (report ? report[String(t).toUpperCase()] : null) || null;
  if (!n && alts.length) {
    out.push('No class name: a code with no name is dropped when the requirements are saved.');
  }
  if (!alts.length) {
    if (!n) return out;
    const r = at(n);
    if (looksLikeCourseCode(n) || (r && r.known)) {
      const held = r && r.known ? ` (${r.holders} ${r.holders === 1 ? 'person holds' : 'people hold'} certificate ${n})` : '';
      out.push(`"${n}" looks like a course code${held}. Put it in the Code box and type the class name here.`);
    }
    return out;
  }
  for (const a of alts) {
    const r = at(a);
    if (r && !r.known) {
      out.push(`No training record on file carries code "${a}". Check it against the Cert # column on Operations -> Training: a code nobody holds reads red for everyone.`);
    }
  }
  return out;
}
